import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import PhotoSubmissionModal from './PhotoSubmissionModal';
import CreditsModal from './CreditsModal';
import './LeaderBoard.css';

const LeaderBoard = () => {
  const navigate = useNavigate();
  // Fixed theme display and refresh issues
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [sounds, setSounds] = useState({});
  const [latestScoreTime, setLatestScoreTime] = useState(null);
  const [shouldScrollToLatest, setShouldScrollToLatest] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Initialize sounds
  useEffect(() => {
    const initializeSounds = () => {
      const soundFiles = {
        shuffle: '/public/sounds/shuffle.mp3'
      };

      const soundObjects = {};
      Object.keys(soundFiles).forEach(soundName => {
        const audio = new Audio(soundFiles[soundName]);
        audio.preload = 'auto';
        soundObjects[soundName] = audio;
      });

      setSounds(soundObjects);
    };

    initializeSounds();
  }, []);

  // Get current user from localStorage
  useEffect(() => {
    const user = localStorage.getItem('currentUser');
    setCurrentUser(user);
  }, []);

  const playSound = (soundName) => {
    if (sounds[soundName]) {
      sounds[soundName].currentTime = 0;
      sounds[soundName].play().catch(err => {
        console.log('Sound play failed:', err);
      });
    }
  };

  // Show top 10 scores
  const getDisplayWinners = () => {
    if (!winners.length) return [];
    
    return winners.slice(0, 10);
  };

  const displayWinners = getDisplayWinners();



  useEffect(() => {
    loadLeaderboard();
    
    // Check if we should scroll to latest score (coming from game)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('scrollToLatest') === 'true') {
      setShouldScrollToLatest(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Scroll to latest score when it's detected
  useEffect(() => {
    if (shouldScrollToLatest && latestScoreTime) {
      const tableWrapper = document.querySelector('.table-wrapper');
      if (tableWrapper) {
        // Add a small delay to ensure the table is rendered
        setTimeout(() => {
          // Smooth scroll to top to show the latest score (which should be first)
          tableWrapper.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          setShouldScrollToLatest(false);
        }, 100);
      }
    }
  }, [shouldScrollToLatest, latestScoreTime]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/leaderboard');
      
      if (!response.ok) {
        throw new Error('Failed to load leaderboard');
      }

      const data = await response.json();
      console.log('Leaderboard API response:', data);
      
      // Validate the response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      const winners = Array.isArray(data.winners) ? data.winners : [];
      console.log('Processed winners data:', winners);
      setWinners(winners);
      
      // Find the most recent score by created_at timestamp
      if (winners.length > 0) {
        const mostRecent = winners.reduce((latest, current) => {
          return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
        });
        
        // Check if this is a new score (different from previous latest)
        if (latestScoreTime && mostRecent.created_at !== latestScoreTime) {
          setShouldScrollToLatest(true);
        }
        
        setLatestScoreTime(mostRecent.created_at);
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      // Redirect to home on API errors to avoid showing JSON
      console.log('API error, redirecting to home');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleNewGame = () => {
    // Play shuffle sound to indicate cards are being randomized
    playSound('shuffle');
    navigate('/game');
  };

  const handlePhotoSubmissions = () => {
    setShowPhotoModal(true);
  };

  const handleShowCredits = () => {
    setShowCreditsModal(true);
  };


  const getPlaceText = (place) => {
    if (place === 1) return '1st';
    if (place === 2) return '2nd';
    if (place === 3) return '3rd';
    return `${place}th`;
  };

  if (loading) {
    return (
      <div className="leader-board">
        <Header 
          title="Leaderboard"
          showLeaderboard={false}
          showNewPlayer={true}
          showNewGame={true}
          onNewGame={handleNewGame}
          onPhotoSubmissions={handlePhotoSubmissions}
          showContributors={true}
          onShowContributors={handleShowCredits}
          showInviteFriends={false}
        />
        
        <div className="leader-board-wrapper">
          <div className="table-wrapper">
            <div className="loading">
              <h2>Loading leaderboard...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Safety check - if winners is not an array, redirect to home
  if (!Array.isArray(winners)) {
    console.error('Invalid winners data, redirecting to home:', winners);
    navigate('/');
    return null;
  }

  return (
    <div className="leader-board">
      <Header 
        title="Leaderboard"
        showLeaderboard={false}
        showNewPlayer={true}
        showNewGame={true}
        onNewGame={handleNewGame}
        onPhotoSubmissions={handlePhotoSubmissions}
        showContributors={true}
        onShowContributors={handleShowCredits}
        showInviteFriends={false}
      />

      <div className="leader-board-wrapper">
        <h2 className="top-scores-heading">🏆 Top 10</h2>
        <div className="table-wrapper">
          {error ? (
            <div className="error-message">
              <h2>{error}</h2>
              <button onClick={loadLeaderboard}>Try Again</button>
            </div>
          ) : winners.length > 0 ? (
            <>
              <table className="leader-board-table">
                <thead>
                  <tr>
                    <th>Place:</th>
                    <th>Name:</th>
                    <th>Seconds Completed:</th>
                  </tr>
                </thead>
                <tbody>
                  {displayWinners.map((winner, index) => {
                    // Validate winner object structure
                    if (!winner || typeof winner !== 'object') {
                      console.error('Invalid winner object:', winner);
                      return null;
                    }
                    
                    const place = index + 1;
                    
                    return (
                      <tr key={index}>
                        <td>
                          {getPlaceText(place)}
                        </td>
                        <td>
                          <span>{winner.user_name || 'Unknown Player'}</span>
                        </td>
                        <td>{winner.game_complete || 0} seconds</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <div className="no-scores">
              <h1>There are no player scores yet!</h1>
              <p>Be the first to complete the game and set a record!</p>
            </div>
          )}
        </div>
      </div>

      <PhotoSubmissionModal 
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
      />

      <CreditsModal 
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
      
    </div>
  );
};

export default LeaderBoard; 